<script setup lang="ts">
import { type Component, computed } from "vue";
import { useRoute } from "vue-router";
import { useStore } from "vuex";
import AiChatWidget from "@/components/AiChatWidget/index.vue";
import ErrorBus from "@/components/ErrorBus.vue";
import LaravelBootstrap from "@/components/LaravelBootstrap.vue";
import AuthLayout from "@/layouts/AuthLayout.vue";
import BareLayout from "@/layouts/BareLayout.vue";
import ExperienceLayout from "@/layouts/ExperienceLayout.vue";
import ExperienceMinimalLayout from "@/layouts/ExperienceMinimalLayout.vue";
import PortalLayout from "@/layouts/PortalLayout.vue";
import PortalMinimalLayout from "@/layouts/PortalMinimalLayout.vue";
import type { LayoutProps } from "@/layouts/types";

/**
 * This gets passed in from `app.js` and populated in Laravel
 */
interface AppProps {
  currentUser?: string;
  learnerTypes?: unknown[];
  oktaToken?: string;
  oktaAnonymousToken?: string;
  brandingDetails?: Record<string, unknown> | unknown[];
  domainEntityId?: string | null;
  ldClient?: string;
  logoUrl?: string;
  logoUrlSquare?: string;
  flags?: Record<string, unknown>;
}

type LayoutVariant = "portal" | "portal-minimal" | "auth" | "experience" | "experience-minimal" | "bare";
type LayoutComponent = Component<LayoutProps>;

const props = withDefaults(defineProps<AppProps>(), {
  oktaToken: "",
  oktaAnonymousToken: "",
  logoUrl: "",
  logoUrlSquare: "",
  ldClient: "",
  learnerTypes: () => [],
  brandingDetails: () => ({}),
  domainEntityId: null,
  flags: () => ({}),
});

const route = useRoute();
const store = useStore();

const layoutVariant = computed<LayoutVariant>(() => {
  const variant = (route.meta?.ui as { layout?: LayoutVariant } | undefined)?.layout;
  return variant ?? "portal";
});

const layoutComponents = {
  bare: BareLayout,
  auth: AuthLayout,
  experience: ExperienceLayout,
  "experience-minimal": ExperienceMinimalLayout,
  "portal-minimal": PortalMinimalLayout,
  portal: PortalLayout,
} satisfies Record<LayoutVariant, LayoutComponent>;

const layoutComponent = computed<LayoutComponent>(() => layoutComponents[layoutVariant.value]);

// Keep Okta token as a fallback since some flows bootstrap only the token.
const isLoggedIn = computed(() => store.getters.isLoggedIn || props.oktaToken?.trim().length > 0);
</script>

<template>
  <laravel-bootstrap
    :current-user="props.currentUser"
    :learner-types="props.learnerTypes"
    :okta-token="props.oktaToken"
    :okta-anonymous-token="props.oktaAnonymousToken"
    :branding-details="props.brandingDetails"
    :domain-entity-id="props.domainEntityId ?? undefined"
  >
    <router-view v-slot="{ Component: RouteComponent }">
      <component
        :is="layoutComponent"
        :is-logged-in="isLoggedIn"
        :ld-client="props.ldClient"
        :logo-url="props.logoUrl"
        :logo-url-square="props.logoUrlSquare"
      >
        <component :is="RouteComponent"></component>
      </component>
    </router-view>
    <error-bus />
    <ai-chat-widget />
  </laravel-bootstrap>
</template>
